import React, {useEffect, useState} from "react";
import {Box, Typography} from "@mui/material";
import dayjs from "dayjs";
import {
    formatDurationBrief,
    FormatTimeRange,
    formatToReadableLargeTitle,
    formatUnixTime,
    FormatUnixTimeForSchedule
} from "../../utils/utils";
import ProjectThumbnailCard, {AudioWaveformIcon, VideoCameraIcon} from "./ProjectThumbnail";
import StatusChip from "../../components/StatusChip";
import DeleteProjectButton from "./DeleteProjectButton";
import './ProjectCard.css';

// Assume these helper functions are defined elsewhere

const dateStyle = {
    fontSize: "13px",
    color: "#808080",
    fontWeight: 400,
    marginTop: "4px",
    textAlign: "center",
};

const styles = {
    cardWrapper: {
        width: '100%',
        cursor: 'pointer',
        transition: 'transform 0.2s ease-in-out',
        ':hover': {
            transform: 'scale(1.02)',
        },
    },
    thumbnailContainer: {
        width: '100%',
        paddingTop: '56.25%', // 16:9 aspect ratio
        position: 'relative',
        backgroundColor: '#E3E5E8',
        borderRadius: '8px',
        overflow: 'hidden',
        marginBottom: '8px',
    },
    sidebarThumbnailContainer: {
        width: '100%',
        paddingTop: '56.25%',
        position: 'relative',
        backgroundColor: '#E3E5E8',
        borderRadius: '8px',
        boxShadow: '1px 1px 1px 1px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
        marginBottom: '8px',
        transition: 'box-shadow 0.2s ease', // Smooth transition
        '&:hover': {
            boxShadow: '1p 1px 1px 1px rgba(0, 0, 0, 0.55)', // Slightly stronger shadow on hover
        },
    },
    durationOverlay: {
        position: 'absolute',
        bottom: 3,
        right: 3,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        color: 'white',
        padding: '4px 8px',
        borderRadius: '4px',
        fontFamily: 'Inter, sans-serif',
        fontSize: '10px',
        fontWeight: 400,
        letterSpacing: '1%',
        lineHeight: '10px',
    },
    thumbnailImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    projectIconContainer: {
        position: 'absolute',
        top: '30%',
        left: '10%',
        transform: 'translate(-10%, -30%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
    },
    iconContainer: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    infoContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    titleContainer: {
        flex: 1,
        marginRight: '8px',
    },
    title: {
        fontFamily: 'Inter',
        fontSize: '13px',
        fontStyle: 'normal',
        fontWeight: '500',
        lineHeight: '14px',
        letterSpacing: '0.16px',
    },
    newSidebarDate: {
        fontFamily: 'Inter',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: '500',
        lineHeight: '14px',
        letterSpacing: '0.16px',
        color: '#7A5EFF',
        backgroundColor: '#EFE9FF'
    },
    date: {
        fontSize: '12px',
        color: '#666',
    },
    sidebarDate: {
        fontSize: '12px',
        color: '#1a1a1a',
    },
};

export const ScheduledProjectThumbnail = ({
                                              title,
                                              scheduledStartTime,
                                              scheduledEndTime,
                                              scheduledTimezone,
                                              createTime,
                                              onClick,
                                              onDeleteClick,
                                          }) => {
    const [aspectRatio, setAspectRatio] = useState('16:9');


    const handleClick = () => {
        onClick();
    }

    const renderThumbnail = () => {
            return (
                <div style={styles.iconContainer}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "14px",
                                color: "#808080",
                                fontWeight: 400,
                            }}
                        >
                            {FormatUnixTimeForSchedule(scheduledStartTime, scheduledTimezone)}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "28px",
                                fontWeight: 700,
                                color: "#000000",
                                lineHeight: "32px",
                            }}
                        >
                            {dayjs.unix(scheduledStartTime).format("DD")}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "14px",
                                color: "#808080",
                                fontWeight: 400,
                            }}
                        >
                            {dayjs.unix(scheduledStartTime).format("MMM").toUpperCase()}
                        </Typography>
                        <Box sx={dateStyle}>
                            {FormatTimeRange(scheduledStartTime, scheduledEndTime, scheduledTimezone)}
                        </Box>
                        <Box sx={dateStyle}>
                            {scheduledTimezone}
                        </Box>
                    </Box>
                </div>
            )
    };

    const hoverOverlay = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        opacity: 0,
        transition: 'opacity 0.2s ease-in-out',
        ':hover': {
            opacity: 1,
        },
    }

    return (
        <Box sx={styles.cardWrapper} className={'project-card'} onClick={handleClick}>
            <Box style={styles.thumbnailContainer}>
                {renderThumbnail()}
                <div style={hoverOverlay}/>
            </Box>
            <Box style={styles.infoContainer}>
                <Box style={styles.titleContainer}>
                    <Box style={styles.title}>{formatToReadableLargeTitle(title)}</Box>
                    <span style={styles.date}>{formatUnixTime(createTime)}</span>
                </Box>
                <Box style={{display: "flex", flexDirection: "column", gap: "2px", alignItems: "center"}}>
                    <Box style={styles.newSidebarDate}>{"Scheduled"}</Box>
                    <Box className="three-dots-button">
                        <DeleteProjectButton handleClick={onDeleteClick} />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

